import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Link } from "react-router-dom";

import { useCallback } from "react";

import { ApiError } from "@/api/client";
import { fetchSystems } from "@/api/endpoints";
import { CardError, CardSkeleton } from "@/components/cards/CardSkeleton";
import { Highlight } from "@/components/Highlight";
import { InfoTip } from "@/components/InfoTip";
import { SearchBox } from "@/components/SearchBox";
import { SortSelect } from "@/components/SortSelect";
import { SystemBadges } from "@/components/SystemBadges";
import { TIPS } from "@/lib/glossary";
import { useInfiniteReveal } from "@/hooks/useInfiniteReveal";
import { useListKeyboardNav } from "@/hooks/useListKeyboardNav";
import { compareBySort, useListParams } from "@/hooks/useListParams";
import { useSystemSummary, type SummaryView } from "@/hooks/useSystemSummary";
import type { SystemSummary } from "@/types/api";
import { usePageTitle } from "@/hooks/usePageTitle";

function matches(s: SystemSummary, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  return (
    s.pws_name.toLowerCase().includes(needle) ||
    s.pws_id.toLowerCase().includes(needle)
  );
}

export function SystemsList(): JSX.Element {
  usePageTitle("Systems");
  const query = useQuery({ queryKey: ["systems"], queryFn: fetchSystems });
  const summary = useSystemSummary();
  const { q, sort, setQ, setSort } = useListParams("name");

  const filteredSorted = useMemo<SystemSummary[]>(() => {
    const all = query.data ?? [];
    const filt = q ? all.filter((s) => matches(s, q)) : all;
    return [...filt].sort((a, b) => compareBySort(sort, a, b, summary.byPwsId));
  }, [query.data, q, sort, summary.byPwsId]);

  const { visible, hasMore, sentinelRef } = useInfiniteReveal(filteredSorted);

  const hrefAt = useCallback(
    (i: number): string | null => {
      const s = visible[i];
      return s ? `/systems/${encodeURIComponent(s.pws_id)}` : null;
    },
    [visible],
  );
  const { focusedIndex, setFocusedIndex, itemRef } = useListKeyboardNav({
    count: visible.length,
    hrefAt,
  });

  if (query.isLoading) return <CardSkeleton title="Your systems" rows={4} />;
  if (query.error) {
    const msg = query.error instanceof ApiError ? query.error.message : "Failed to load";
    return <CardError title="Your systems" message={msg} />;
  }

  const total = query.data?.length ?? 0;

  return (
    <div className="space-y-4">
      {summary.loaded && summary.totalHigh > 0 && <ComplianceBanner summary={summary} />}

      <section className="card">
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h1 className="text-lg font-semibold inline-flex items-center">
              Your systems
              <InfoTip {...TIPS.alertBadges} />
            </h1>
            <p className="text-sm text-ink-300">
              {q ? `${filteredSorted.length} of ${total} match` : `${total} assigned`}
            </p>
          </div>
          <div className="flex items-center gap-3 sm:w-auto w-full">
            <SortSelect value={sort} onChange={setSort} />
            <div className="flex-1 sm:w-72">
              <SearchBox value={q} onChange={setQ} placeholder="Search by name or PWS ID" />
            </div>
          </div>
        </header>

        {filteredSorted.length === 0 ? (
          <p className="text-sm text-ink-300">
            {total === 0 ? "No systems assigned yet." : "No systems match that search."}
          </p>
        ) : (
          <>
            <ul className="divide-y divide-ink-700">
              {visible.map((s, i) => (
                <li key={s.pws_id} ref={itemRef(i)}>
                  <Link
                    to={`/systems/${encodeURIComponent(s.pws_id)}`}
                    onMouseEnter={() => setFocusedIndex(i)}
                    className={`block py-3 -mx-2 px-2 rounded transition-colors ${
                      focusedIndex === i
                        ? "bg-ink-700/40 ring-1 ring-ink-500"
                        : "hover:bg-ink-700/30"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">
                          <Highlight text={s.pws_name} query={q} />
                        </p>
                        <p className="text-xs text-ink-300 font-mono">
                          PWS #<Highlight text={s.pws_id} query={q} />
                        </p>
                      </div>
                      <SystemBadges
                        enriched={summary.byPwsId.get(s.pws_id)}
                        loading={summary.loading}
                      />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
            {hasMore && (
              <div ref={sentinelRef} className="py-4 text-center text-xs text-ink-400">
                Loading more…
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function ComplianceBanner({ summary }: { summary: SummaryView }): JSX.Element {
  return (
    <section className="card ring-severity-high/40 bg-severity-high/5">
      <p className="text-sm">
        <span className="pill-high mr-2">{summary.totalHigh} HIGH</span>
        across {summary.systemsWithHigh} system{summary.systemsWithHigh === 1 ? "" : "s"}
        {summary.totalMedium > 0 && (
          <>
            {" "}· <span className="pill-medium">{summary.totalMedium} MEDIUM</span>
          </>
        )}
        {summary.totalLow > 0 && (
          <>
            {" "}· <span className="pill-low">{summary.totalLow} LOW</span>
          </>
        )}
      </p>
    </section>
  );
}
